import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { updateAdminApp, type AdminRegistryApp } from '@/api/console';
import { Button, Callout, Modal, useToast } from '@/components/ui';
import { hasFailedProbe } from '../AppDetail/approvalGate';
import { ConnectionCheckResult } from '../AppDetail/ConnectionCheckResult';
import { useAppApproval } from '../AppDetail/useAppApproval';

interface FlipAppModalProps {
    /** The app pending the enable/disable confirm; `null` closes the modal. */
    app: AdminRegistryApp | null;
    /**
     * EAA-1817: `app.role_link_count` — the server-computed aggregate on
     * `GET /admin/apps` (`ApplicationService.registry_aggregates()`), not a
     * client-side count. The previous version of this modal took a
     * `roleLinkCount` the PARENT derived from a separate `/role-maps` fetch;
     * that derivation is gone now that the registry row carries the real
     * number itself, so the parent forwards `app.role_link_count` directly.
     */
    roleLinkCount: number;
    /** EAA-1709: `console.apps.credentials` — the connection check's server gate. */
    canCheckConnection: boolean;
    onClose: () => void;
}

/**
 * EAA-1819/EAA-1817: shared enable/disable confirm modal for `/console/apps`.
 *
 * Enabling an app IS the production approval gate (D11) — it therefore gets
 * the same destructive-style confirm as disabling (`portal-btn-danger`, not
 * the plain primary button the previous version used for "Duyệt & bật"), and
 * a mandatory warning when the app has zero role links, because that app
 * would go live reachable-but-powerless.
 *
 * Disabling states its consequence WITHOUT a user count: portal-api's
 * `accounts` resource has no GET/list endpoint at all (verified against
 * services/portal-api), so there is no way to source "N người dùng sẽ mất
 * tile" truthfully today. EAA-1829 is the ticket that would add such a count;
 * until then this says what happens, not to how many people.
 *
 * EAA-1709: ENABLING now goes through check → approve, the same gate as the
 * app-detail header (`useAppApproval`): "Duyệt & bật" appears only after a
 * connection check in this dialog returned no `failed` probe, and it calls
 * `POST …/approve` (which re-runs the check server-side) instead of PATCHing
 * `enabled`. Disabling is unchanged.
 */
export function FlipAppModal({
    app,
    roleLinkCount,
    canCheckConnection,
    onClose,
}: FlipAppModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const flipMutation = useMutation({
        mutationFn: (target: AdminRegistryApp) =>
            updateAdminApp(config.apiBase, accessToken as string, target.code, {
                enabled: !target.enabled,
            }),
        onSuccess: (_data, target) => {
            // EAA-1845: `target` is the PRE-mutation app object (see the interface
            // doc above) — `target.enabled === true` means the action just performed
            // was disable ("tắt"), and vice versa. Keep the two toast keys' polarity
            // matched to this when editing either.
            toast.success(
                t(
                    target.enabled
                        ? 'console.apps.flipModal.toastDisabled'
                        : 'console.apps.flipModal.toastEnabled',
                    { name: target.name, code: target.code }
                )
            );
            onClose();
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            // The launcher's enabled-only registry changed too.
            void queryClient.invalidateQueries({ queryKey: ['apps-registry'] });
            // EAA-1819: role-map sync-status's health counters derive from the
            // same registry data — every mutation here invalidates it too.
            void queryClient.invalidateQueries({ queryKey: ['role-map-sync-status'] });
        },
        onError: (_error, target) => {
            toast.error(
                t('console.apps.flipModal.toastError', { name: target.name, code: target.code })
            );
        },
    });

    const isEnabling = app !== null && !app.enabled;
    const approval = useAppApproval(isEnabling ? app : null, {
        canCheck: canCheckConnection,
        canApprove: true,
        onApproved: () => {
            approval.invalidate();
            onClose();
        },
    });
    const busy = flipMutation.isPending || approval.approving;

    function handleClose() {
        if (busy) return;
        approval.invalidate();
        onClose();
    }

    let enableHint = 'console.apps.flipModal.checkFirstHint';
    if (!canCheckConnection) enableHint = 'console.apps.flipModal.checkPermissionHint';
    else if (approval.report && approval.stale) enableHint = 'console.appDetail.approval.hintStale';
    else if (approval.report && hasFailedProbe(approval.report)) {
        enableHint = 'console.appDetail.approval.hintFailed';
    } else if (approval.report) enableHint = 'console.appDetail.approval.hintReady';

    return (
        <Modal
            open={app !== null}
            onCancel={handleClose}
            title={
                isEnabling
                    ? t('console.apps.flipModal.enableTitle')
                    : t('console.apps.flipModal.disableTitle')
            }
            footer={
                // EAA-1804: raw `<button className="portal-btn[-danger]">` -> kit
                // `Button` (props-for-classes; see AccountDetailModal.tsx for the
                // mapping rationale). This confirm is destructive either way
                // (enable is the production approval gate, disable removes access),
                // so both actions read as `danger` per the docstring above.
                <>
                    <Button onClick={handleClose} disabled={busy}>
                        {t('common.cancel')}
                    </Button>
                    {isEnabling && approval.canRunCheck && (
                        <Button onClick={approval.runCheck} disabled={approval.checking || busy}>
                            {approval.checking
                                ? t('console.appDetail.connection.check.pending')
                                : t('console.appDetail.connection.check.button')}
                        </Button>
                    )}
                    {isEnabling && approval.canApprove && (
                        <Button
                            danger
                            onClick={approval.approve}
                            disabled={busy || approval.checking}
                        >
                            {approval.approving
                                ? t('console.apps.flipModal.processingLabel')
                                : t('console.apps.flipModal.enableButton')}
                        </Button>
                    )}
                    {!isEnabling && (
                        <Button
                            danger
                            onClick={() => app && flipMutation.mutate(app)}
                            disabled={flipMutation.isPending}
                        >
                            {flipMutation.isPending
                                ? t('console.apps.flipModal.processingLabel')
                                : t('console.apps.flipModal.disableTitle')}
                        </Button>
                    )}
                </>
            }
        >
            {app && (
                <>
                    <p>
                        {isEnabling
                            ? t('console.apps.flipModal.enableBody', {
                                  name: app.name,
                                  code: app.code,
                              })
                            : t('console.apps.flipModal.disableBody', {
                                  name: app.name,
                                  code: app.code,
                              })}
                    </p>
                    {isEnabling && roleLinkCount === 0 && (
                        // EAA-1817: label copy verbatim from the frame 45 export ("Trước khi
                        // bật:") — was "Chưa có liên kết vai trò", which said the same fact but
                        // not in the design's words.
                        <Callout
                            tone="warning"
                            label={t('console.apps.flipModal.beforeEnableWarningLabel')}
                        >
                            {t('console.apps.flipModal.beforeEnableWarningBody')}
                        </Callout>
                    )}
                    {isEnabling && (
                        <p className="portal-admin-hint" data-testid="flip-enable-hint">
                            {t(enableHint)}
                        </p>
                    )}
                    {isEnabling && approval.report && (
                        <ConnectionCheckResult report={approval.report} />
                    )}
                </>
            )}
        </Modal>
    );
}
